import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { adminAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Logo } from './Logo';
import {
  Scissors,
  Lock,
  LogOut,
  Users,
  Phone,
  Clock,
  Check,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  MessageSquare,
} from 'lucide-react';

const BARBERS = ['All', 'Pete', 'Mckee', 'Chris', 'First Available'];
const SERVICE_DURATIONS = {
  'Classic Haircut': 20,
  'Beard Trim': 15,
  'Haircut & Beard Combo': 30,
  'Kids Haircut': 20,
};
const DEFAULT_DURATION = 20;

const getDuration = (service) => SERVICE_DURATIONS[service] || DEFAULT_DURATION;

// ===== Login Screen =====
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await adminAPI.login(password);
      toast({ title: 'Welcome!', description: 'Login successful.' });
      onLogin();
    } catch (err) {
      toast({
        title: 'Login Failed',
        description: 'Incorrect password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-700 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white border-stone-300 shadow-2xl">
        <CardContent className="p-8 space-y-6">
          <div className="text-center space-y-3">
            <Logo variant="full-dark" className="h-24 w-auto mx-auto" />
            <h1 className="text-2xl font-bold text-stone-900 pt-2">Admin Access</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-stone-900 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  className="pl-10 border-stone-300 focus:border-maroon-700 focus:ring-maroon-700"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-maroon-800 hover:bg-maroon-900 text-stone-100 py-6 text-lg font-semibold"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <a
            href="/"
            className="block text-center text-sm text-stone-500 hover:text-maroon-800 transition-colors"
          >
            ← Back to site
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

// ===== Waitlist Card =====
const WaitlistCard = ({ entry, position, estimatedWait, onServe, onMove, canMoveUp, canMoveDown }) => {
  const joinedTime = new Date(entry.joined_at);
  const joinedFormatted = joinedTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const minutesAgo = Math.floor((new Date() - joinedTime) / 60000);

  return (
    <Card className="bg-white border-stone-200 hover:border-maroon-700 transition-all duration-200 hover:shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Up/Down controls */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                onClick={() => onMove(entry.id, 'up')}
                disabled={!canMoveUp}
                className="p-1 rounded hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Move up"
                data-testid={`move-up-${entry.id}`}
              >
                <ChevronUp className="h-4 w-4 text-stone-700" />
              </button>
              <div className="bg-maroon-800 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-stone-100 font-bold">#{position}</span>
              </div>
              <button
                onClick={() => onMove(entry.id, 'down')}
                disabled={!canMoveDown}
                className="p-1 rounded hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Move down"
                data-testid={`move-down-${entry.id}`}
              >
                <ChevronDown className="h-4 w-4 text-stone-700" />
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-stone-900 break-words">{entry.name}</h3>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-stone-600">
                <a
                  href={`tel:${entry.phone}`}
                  className="flex items-center gap-1.5 hover:text-maroon-800 transition-colors"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {entry.phone}
                </a>
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {entry.party_size} {entry.party_size === 1 ? 'person' : 'people'}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Joined {joinedFormatted} ({minutesAgo}m ago)
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-maroon-100 text-maroon-900">
                  <Scissors className="h-3 w-3" />
                  {entry.barber}
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-stone-200 text-stone-700">
                  {entry.service || 'Classic Haircut'}
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-stone-200 text-stone-700">
                  ~{estimatedWait} min wait
                </span>
              </div>
              {entry.message && (
                <div className="mt-3 flex items-start gap-2 p-3 rounded bg-amber-50 border border-amber-200" data-testid={`note-${entry.id}`}>
                  <MessageSquare className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-stone-800 break-words">{entry.message}</p>
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={() => onServe(entry.id)}
            className="bg-maroon-800 hover:bg-maroon-900 text-stone-100"
            data-testid={`serve-btn-${entry.id}`}
          >
            <Check className="h-4 w-4 mr-2" />
            Mark Served
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Dashboard =====
const Dashboard = ({ onLogout }) => {
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBarber, setSelectedBarber] = useState('All');
  const { toast } = useToast();

  const fetchWaitlist = useCallback(async () => {
    try {
      const data = await adminAPI.getWaitlist();
      setWaitlist(data);
    } catch (err) {
      if (err.response?.status === 401) {
        adminAPI.logout();
        onLogout();
      } else {
        toast({
          title: 'Failed to load waitlist',
          description: 'Please try refreshing.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [onLogout, toast]);

  useEffect(() => {
    fetchWaitlist();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchWaitlist, 30000);
    return () => clearInterval(interval);
  }, [fetchWaitlist]);

  const handleServe = async (entryId) => {
    try {
      await adminAPI.markServed(entryId);
      toast({ title: 'Customer marked as served' });
      fetchWaitlist();
    } catch (err) {
      toast({
        title: 'Failed to update',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleMove = async (entryId, direction) => {
    try {
      await adminAPI.moveEntry(entryId, direction);
      await fetchWaitlist();
    } catch (err) {
      toast({
        title: 'Could not move',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    adminAPI.logout();
    onLogout();
  };

  const SPECIFIC_BARBERS_LIST = ['Pete', 'Mckee', 'Chris'];

  // Each barber (including First Available) has its own independent queue
  const getEffectiveQueue = (barber) => {
    return waitlist
      .filter(e => e.barber === barber)
      .sort((a, b) => (a.joined_at || '').localeCompare(b.joined_at || ''));
  };

  // Filter waitlist by selected barber
  const filteredList = selectedBarber === 'All'
    ? waitlist
    : getEffectiveQueue(selectedBarber);

  // Estimated wait time = sum of service durations for entries ahead in the same queue
  const getEstimatedWait = (entry) => {
    const queue = getEffectiveQueue(entry.barber);
    const idx = queue.findIndex(e => e.id === entry.id);
    const ahead = queue.slice(0, idx);
    return ahead.reduce(
      (sum, e) => sum + getDuration(e.service) * (e.party_size || 1),
      0
    );
  };

  // Count per barber (independent queues)
  const counts = {
    Pete: waitlist.filter(e => e.barber === 'Pete').length,
    Mckee: waitlist.filter(e => e.barber === 'Mckee').length,
    Chris: waitlist.filter(e => e.barber === 'Chris').length,
    'First Available': waitlist.filter(e => e.barber === 'First Available').length,
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-stone-700 border-b border-stone-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="full-light" className="h-12 w-auto" />
            <p className="text-xs text-stone-400 border-l border-stone-500 pl-3 hidden sm:block">
              Waitlist Dashboard
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={fetchWaitlist}
              variant="outline"
              size="sm"
              className="border-stone-500 text-stone-100 hover:bg-stone-600 hover:text-stone-100"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-stone-500 text-stone-100 hover:bg-stone-600 hover:text-stone-100"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-maroon-800 text-stone-100 border-none">
            <CardContent className="p-5">
              <p className="text-sm text-rose-100 uppercase tracking-wide">Total Waiting</p>
              <p className="text-3xl font-bold mt-1" data-testid="total-count">{waitlist.length}</p>
            </CardContent>
          </Card>
          {['Pete', 'Mckee', 'Chris', 'First Available'].map((barber) => (
            <Card key={barber} className="bg-white border-stone-200">
              <CardContent className="p-5">
                <p className="text-sm text-stone-600 uppercase tracking-wide truncate">{barber}</p>
                <p className="text-3xl font-bold mt-1 text-stone-900">{counts[barber]}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Barber Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {BARBERS.map((barber) => (
            <button
              key={barber}
              onClick={() => setSelectedBarber(barber)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                selectedBarber === barber
                  ? 'bg-maroon-800 text-stone-100'
                  : 'bg-white text-stone-700 border border-stone-300 hover:border-maroon-700'
              }`}
              data-testid={`filter-${barber.toLowerCase().replace(' ', '-')}`}
            >
              {barber}
              {barber !== 'All' && counts[barber] > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {counts[barber]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Waitlist */}
        {isLoading ? (
          <div className="text-center py-12 text-stone-500">Loading waitlist...</div>
        ) : filteredList.length === 0 ? (
          <Card className="bg-white border-stone-200">
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">
                {selectedBarber === 'All' ? 'No customers waiting' : `No customers waiting for ${selectedBarber}`}
              </h3>
              <p className="text-stone-500">
                When customers join the waitlist, they'll appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredList.map((entry, idx) => {
              // can move up if not first in their barber's queue
              const barberQueue = getEffectiveQueue(entry.barber);
              const posInBarber = barberQueue.findIndex(e => e.id === entry.id);
              const canMoveUp = posInBarber > 0;
              const canMoveDown = posInBarber >= 0 && posInBarber < barberQueue.length - 1;
              return (
                <WaitlistCard
                  key={entry.id}
                  entry={entry}
                  position={idx + 1}
                  estimatedWait={getEstimatedWait(entry)}
                  onServe={handleServe}
                  onMove={handleMove}
                  canMoveUp={canMoveUp}
                  canMoveDown={canMoveDown}
                />
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-stone-500 mt-8">
          Waitlist auto-clears daily. Auto-refreshes every 30 seconds.
        </p>
      </main>
    </div>
  );
};

// ===== Main Admin Page =====
export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(adminAPI.isAuthenticated());

  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  return <Dashboard onLogout={() => setIsAuthenticated(false)} />;
};

export default AdminPage;
